/* ========== BAR CHARTS (horizontal) ==========
 * Subtypes:
 *  - Clustered Bar (drawHorizontalBarChart)
 *  - Stacked Bar (drawStackedBarChart, isPercent=false)
 *  - 100% Stacked Bar (drawStackedBarChart, isPercent=true)
 *  - 3-D Bar variants (is3D=true — box bars + oblique axis like Excel)
 *
 * Excel conventions implemented here:
 *  - categories run BOTTOM-TO-TOP (first category at the bottom)
 *  - default gap width 150% → bars fill ~40% of their slot
 *  - multi-level category labels: leaf level horizontal next to the axis,
 *    parent levels as rotated (vertical) text columns further left, boxed
 *  - 3-D: bars are boxes (front + top + end face), vertical gridlines
 *    lean with the depth direction, value labels descend to the right
 * ============================================== */

import {
  COLORS,
  drawLegend,
  shadeColor,
} from './utils';

var BAR_FRACTION = 0.4; // Excel default gapWidth=150% → 1/(1+1.5)

function fitLabel(ctx, label, maxW) {
  if (ctx.measureText(label).width <= maxW) return label;
  var l = label;
  while (l.length > 1 && ctx.measureText(l + '…').width > maxW) l = l.slice(0, -1);
  return l + '…';
}

/** Category slot top for category i — first category at the BOTTOM. */
function slotTopFor(i, y, slotH, numCategories) {
  return y + (numCategories - 1 - i) * slotH;
}

/**
 * Y-axis category labels. Multi-level axes render like Excel: boxed rows,
 * leaf labels horizontal beside the axis, parent levels rotated 90° in
 * narrow columns to the left (merged across repeated values).
 */
function drawYAxisLabels(ctx, x, y, height, cats, catLevels, numCategories) {
  var slotH = height / numCategories;
  var leafLabels = catLevels ? catLevels[0] : cats;
  ctx.font = '9px Arial';

  function centerY(i) { return slotTopFor(i, y, slotH, numCategories) + slotH / 2; }

  if (!catLevels || catLevels.length < 2) {
    for (var i = 0; i < numCategories; i++) {
      var lbl = leafLabels && leafLabels[i] != null && leafLabels[i] !== '' ? '' + leafLabels[i] : '' + (i + 1);
      ctx.fillStyle = '#666';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(lbl, x - 6, centerY(i));
    }
    return;
  }

  // ── Multi-level: leaf band + one rotated column per parent level ──
  var maxLeafW = 0;
  for (var mi = 0; mi < numCategories; mi++) {
    var ml = leafLabels[mi] != null ? '' + leafLabels[mi] : '';
    var w = ctx.measureText(ml).width;
    if (w > maxLeafW) maxLeafW = w;
  }
  var leafBandW = Math.min(90, maxLeafW + 14);
  var lvlColW = 16;
  var extraLevels = catLevels.length - 1;
  var bandW = leafBandW + extraLevels * lvlColW;
  var bandLeft = x - bandW;

  // Boxes: row separators + level-column separators (Excel's boxed axis)
  ctx.strokeStyle = '#D9D9D9';
  ctx.lineWidth = 1;
  for (var r = 0; r <= numCategories; r++) {
    var ry = y + r * slotH;
    ctx.beginPath();
    ctx.moveTo(bandLeft, ry);
    ctx.lineTo(x, ry);
    ctx.stroke();
  }
  for (var lc = 0; lc <= extraLevels; lc++) {
    var lx = bandLeft + lc * lvlColW;
    ctx.beginPath();
    ctx.moveTo(lx, y);
    ctx.lineTo(lx, y + height);
    ctx.stroke();
  }

  // Leaf labels: horizontal, right-aligned beside the axis
  for (var li = 0; li < numCategories; li++) {
    var leaf = leafLabels[li] != null && leafLabels[li] !== '' ? '' + leafLabels[li] : '' + (li + 1);
    ctx.fillStyle = '#666';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(fitLabel(ctx, leaf, leafBandW - 8), x - 6, centerY(li));
  }

  // Parent levels: rotated 90°, one narrow column per level (level 1
  // closest to the leaf band, deeper levels further left)
  for (var lv = 1; lv < catLevels.length; lv++) {
    var colCx = x - leafBandW - (lv - 1) * lvlColW - lvlColW / 2;
    for (var ci = 0; ci < numCategories; ci++) {
      var plbl = catLevels[lv][ci] != null ? '' + catLevels[lv][ci] : '';
      if (!plbl) continue;
      // Skip repeats of the row below (drawn bottom-up like Excel spans)
      if (ci > 0 && ('' + catLevels[lv][ci - 1]) === plbl) continue;
      ctx.save();
      ctx.translate(colCx, centerY(ci));
      ctx.rotate(-Math.PI / 2);
      ctx.fillStyle = '#666';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(fitLabel(ctx, plbl, slotH - 4), 0, 0);
      ctx.restore();
    }
  }
}

/** Bottom value axis: gridlines + tick labels. 3-D leans/descends. */
function drawValueAxis(ctx, x, y, width, height, minValue, range, isPercent, is3D) {
  var depthLean = is3D ? 14 : 0;
  var downSlope = is3D ? 0.045 : 0;
  ctx.lineWidth = 1;
  for (var i = 0; i <= 5; i++) {
    var gx = x + (i / 5) * width;
    ctx.strokeStyle = '#E0E0E0';
    ctx.beginPath();
    ctx.moveTo(gx, y + height + (is3D ? (gx - x) * downSlope : 0));
    ctx.lineTo(gx + depthLean, y - (is3D ? 8 : 0));
    ctx.stroke();
    var val = minValue + (i / 5) * range;
    var txt = isPercent
      ? Math.round(val * 100) + '%'
      : (val % 1 === 0 ? val.toString() : val.toFixed(1));
    ctx.fillStyle = '#888';
    ctx.font = '9px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(txt, gx, y + height + 5 + (gx - x) * downSlope);
  }
}

/** Draw one horizontal 3-D box bar (front + top + end faces). */
function draw3DBarBox(ctx, bx, by, len, barH, color, d) {
  if (len <= 0) len = 0.5;
  // Top face
  ctx.fillStyle = shadeColor(color, 18);
  ctx.beginPath();
  ctx.moveTo(bx, by);
  ctx.lineTo(bx + len, by);
  ctx.lineTo(bx + len + d, by - d);
  ctx.lineTo(bx + d, by - d);
  ctx.closePath();
  ctx.fill();
  // End cap
  ctx.fillStyle = shadeColor(color, -22);
  ctx.beginPath();
  ctx.moveTo(bx + len, by);
  ctx.lineTo(bx + len + d, by - d);
  ctx.lineTo(bx + len + d, by + barH - d);
  ctx.lineTo(bx + len, by + barH);
  ctx.closePath();
  ctx.fill();
  // Front face
  ctx.fillStyle = color;
  ctx.fillRect(bx, by, len, barH);
}

export function drawHorizontalBarChart(ctx, x, y, width, height, chartObj, is3D) {
  var seriesArr = chartObj && chartObj.series ? chartObj.series : [];
  if (seriesArr.length === 0) return;
  var numSeries = seriesArr.length;
  var numCategories = Math.max.apply(null, seriesArr.map(function (s) { return s.data.length; }));
  if (numCategories === 0) return;

  // Bar value axis starts at 0 (Excel bar charts are zero-based)
  var allVals = seriesArr.flatMap(function (s) { return s.data; });
  var maxValue = Math.max.apply(null, allVals.concat([0])) || 100;
  var minValue = Math.min.apply(null, allVals.concat([0]));
  var range = maxValue - minValue || 100;

  var slotH = height / numCategories;
  var barBand = slotH * BAR_FRACTION;
  var barH = barBand / numSeries;
  var bandPad = (slotH - barBand) / 2;
  var d = is3D ? Math.max(4, Math.min(9, barH * 0.9)) : 0;
  var cats = chartObj.categories || [];

  drawValueAxis(ctx, x, y, width, height, minValue, range, false, is3D);

  seriesArr.forEach(function (series, sIdx) {
    var color = series.color || COLORS[sIdx % COLORS.length];
    series.data.forEach(function (value, cIdx) {
      var len = ((value - minValue) / range) * width;
      var barY = slotTopFor(cIdx, y, slotH, numCategories) + bandPad + sIdx * barH;
      if (is3D) {
        draw3DBarBox(ctx, x, barY, len, barH, color, d);
      } else {
        ctx.fillStyle = color;
        ctx.fillRect(x, barY, len, barH);
      }
    });
  });

  drawYAxisLabels(ctx, x, y, height, cats, chartObj.catLevels, numCategories);

  // Axes
  ctx.strokeStyle = '#999';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + height);
  if (!is3D) ctx.lineTo(x + width, y + height);
  ctx.stroke();

  drawLegend(ctx, x, y, width, height, seriesArr);
}

export function drawStackedBarChart(ctx, x, y, width, height, chartObj, isPercent, is3D) {
  var seriesArr = chartObj && chartObj.series ? chartObj.series : [];
  if (seriesArr.length === 0) return;
  var numCategories = Math.max.apply(null, seriesArr.map(function (s) { return s.data.length; }));
  if (numCategories === 0) return;
  var cats = chartObj.categories || [];

  var totals = new Array(numCategories).fill(0);
  for (var c = 0; c < numCategories; c++) {
    seriesArr.forEach(function (s) { totals[c] += s.data[c] || 0; });
  }
  var maxTotal = isPercent ? 1 : Math.max.apply(null, totals.concat([1]));
  var minValue = 0;
  var range = maxTotal - minValue || 1;

  var slotH = height / numCategories;
  var barH = slotH * BAR_FRACTION;
  var bandPad = (slotH - barH) / 2;
  var d = is3D ? Math.max(4, Math.min(9, barH * 0.9)) : 0;

  drawValueAxis(ctx, x, y, width, height, minValue, range, isPercent, is3D);

  for (var ci = 0; ci < numCategories; ci++) {
    var runningSum = 0;
    var categoryTotal = totals[ci] || 1;
    var barY = slotTopFor(ci, y, slotH, numCategories) + bandPad;
    /* eslint-disable no-loop-func */
    seriesArr.forEach(function (series, sIdx) {
      var v = series.data[ci] || 0;
      var displayVal = isPercent ? (v / (categoryTotal || 1)) : v;
      var segLen = (displayVal / range) * width;
      var segX = x + (runningSum / range) * width;
      var color = series.color || COLORS[sIdx % COLORS.length];

      if (is3D) {
        draw3DBarBox(ctx, segX, barY, segLen, barH, color, d);
      } else {
        ctx.fillStyle = color;
        ctx.fillRect(segX, barY, segLen, barH);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 0.5;
        ctx.strokeRect(segX, barY, segLen, barH);
      }
      runningSum += displayVal;
    });
    /* eslint-enable no-loop-func */
  }

  drawYAxisLabels(ctx, x, y, height, cats, chartObj.catLevels, numCategories);

  ctx.strokeStyle = '#999';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + height);
  if (!is3D) ctx.lineTo(x + width, y + height);
  ctx.stroke();

  drawLegend(ctx, x, y, width, height, seriesArr);
}
